'use client';

/**
 * Hook for the usage-based tier recommendation (#5554).
 *
 * Fetches GET /users/tier-recommendation and exposes the result for
 * pricing surfaces (PricingV2Section, ProfessionalPricingSection) to
 * render an 'Odporúčané pre vás' badge on the right card.
 *
 * Usage:
 *   const { recommendation, isLoading } = useTierRecommendation();
 *
 * Returns:
 *   recommendation  — TierRecommendation when available; null when not
 *                     authenticated or when there is no trial usage.
 *   isLoading       — true while the request is in flight.
 *
 * Design notes:
 *   - Only fires the API call when the user is authenticated. Unauthenticated
 *     visitors (common on /pricing) see no badge — avoids a noisy 401 in the
 *     network log.
 *   - Fails soft: a network error or non-200 response silently sets
 *     recommendation=null. The badge not showing is a safe degradation;
 *     the pricing page functions fully without it.
 */

import { useAuthUser } from '@/hooks/useAuthUser';
import tierRecommendationService, {
  type TierRecommendation,
} from '@/services/tier-recommendation-service';
import { useCallback, useEffect, useState } from 'react';

interface TierRecommendationState {
  /** Null when unauthenticated, loading, or no trial usage data. */
  recommendation: TierRecommendation | null;
  isLoading: boolean;
}

/**
 * Fetch the authenticated user's tier recommendation derived from their
 * real trial usage. Returns null for unauthenticated visitors.
 */
export function useTierRecommendation(): TierRecommendationState {
  const { isAuthenticated, isLoading: authLoading } = useAuthUser();

  const [recommendation, setRecommendation] =
    useState<TierRecommendation | null>(null);
  const [recLoading, setRecLoading] = useState(false);

  const fetchRecommendation = useCallback(async (): Promise<void> => {
    if (!isAuthenticated) {
      setRecommendation(null);
      return;
    }
    setRecLoading(true);
    try {
      // resp is the unwrapped ApiResp envelope from the api-services interceptor.
      const resp = await tierRecommendationService.getRecommendation();
      // Only set recommendation on explicit success with a non-null payload.
      if (resp?.status === 'success' && resp.data != null) {
        setRecommendation(resp.data);
      } else {
        setRecommendation(null);
      }
    } catch {
      // Fail soft — recommendation unavailable is not a critical error.
      setRecommendation(null);
    } finally {
      setRecLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    void fetchRecommendation();
  }, [fetchRecommendation]);

  return {
    recommendation,
    isLoading: authLoading || recLoading,
  };
}
